<template>
  <v-card outlined :loading="loading || saving">
    <v-card-title class="text-h6">
      <v-icon color="primary" class="mr-2">mdi-calendar-clock</v-icon>ช่วงเวลาพิเศษตั้งค่าตารางเรียน
      <v-spacer />
      <v-btn text :disabled="busy" @click="reload"><v-icon left>mdi-refresh</v-icon>โหลดใหม่</v-btn>
    </v-card-title>
    <v-card-text>
      <v-row>
        <v-col cols="12" sm="6" md="3"><v-select v-model="level" :items="levels" label="ระดับการศึกษา" outlined dense hide-details :disabled="busy || dialog" /></v-col>
        <v-col cols="12" sm="6" md="4"><v-autocomplete v-model="faculty" :items="faculties" label="คณะ" outlined dense hide-details :disabled="busy || dialog" /></v-col>
        <v-col cols="6" md="3"><v-text-field v-model="year" label="ปีการศึกษา (พ.ศ.)" maxlength="4" inputmode="numeric" outlined dense hide-details :disabled="busy || dialog" @change="scopeChanged" /></v-col>
        <v-col cols="6" md="2"><v-select v-model="semester" :items="semesters" label="ภาคเรียน" outlined dense hide-details :disabled="busy || dialog" /></v-col>
      </v-row>
      <div class="d-flex align-center my-4">
        <span>{{ total }} รายการ</span><v-spacer />
        <v-btn color="primary" :disabled="busy || !scopeReady || !ready" @click="openCreate"><v-icon left>mdi-plus</v-icon>เพิ่มช่วงเวลาพิเศษ</v-btn>
      </div>
      <v-data-table :headers="headers" :items="rows" :loading="loading" item-key="ID" hide-default-footer :items-per-page="25"
        no-data-text="ไม่มีช่วงเวลาพิเศษในคณะและภาคเรียนที่เลือก" loading-text="กำลังโหลดข้อมูล" disable-sort>
        <template v-slot:[`item.TARGET_TYPE`]="{ item }">{{ item.TARGET_TYPE === 'FACULTY' ? 'ทุกคนที่มีสิทธิ์' : `${item.USER_COUNT} คน` }}</template>
        <template v-slot:[`item.period`]="{ item }"><div class="py-2">{{ formatDate(item.START_DATETIME) }}<br>ถึง {{ formatDate(item.END_DATETIME) }}</div></template>
        <template v-slot:[`item.STATUS`]="{ item }"><v-chip small outlined :color="statusInfo(item.STATUS).color">{{ statusInfo(item.STATUS).text }}</v-chip></template>
        <template v-slot:[`item.actions`]="{ item }">
          <v-btn small text :disabled="busy" @click="openDetail(item)">{{ item.deleted_at ? 'ดูรายละเอียด' : 'ดู / แก้ไข' }}</v-btn>
        </template>
      </v-data-table>
      <v-pagination v-if="total > 25" v-model="page" :length="Math.ceil(total / 25)" :total-visible="7" :disabled="busy" class="mt-4" @input="reload" />
    </v-card-text>
    <v-dialog v-model="dialog" max-width="800" persistent scrollable>
      <v-card>
        <v-card-title>{{ form.id ? 'รายละเอียดช่วงเวลาพิเศษ' : 'เพิ่มช่วงเวลาพิเศษ' }}</v-card-title>
        <v-card-text>
          <p class="font-weight-medium">{{ scopeLabel }}</p>
          <v-radio-group v-model="form.target_type" row :disabled="saving || readonly" @change="targetChanged">
            <v-radio label="ทุกคนที่มีสิทธิ์" value="FACULTY" /><v-radio label="เลือกรายบุคคล" value="USERS" />
          </v-radio-group>
          <template v-if="form.target_type === 'USERS'">
            <v-autocomplete v-model="form.users" :items="userOptions" item-text="label" item-value="USER_ID" multiple chips deletable-chips
              label="ผู้มีสิทธิ์ตั้งค่าตารางเรียน" outlined hide-details :loading="usersLoading" :disabled="saving || readonly || usersLoading"
              :search-input.sync="userSearch" @change="userSearch = ''" no-data-text="ไม่พบผู้มีสิทธิ์ในคณะและระดับนี้" />
            <div class="d-flex align-center mb-4 mt-2"><span>เลือก {{ form.users.length }} คน</span><v-spacer />
              <v-btn small text :disabled="readonly || saving || usersLoading || !usersReady" @click="selectAll">เลือกทั้งหมดในรายชื่อ</v-btn>
              <v-btn small text :disabled="readonly || saving" @click="form.users = []">ล้างที่เลือก</v-btn>
            </div>
          </template>
          <v-row>
            <v-col cols="12" sm="6">
              <v-menu v-model="startMenu" :close-on-content-click="false" offset-y min-width="290px" :disabled="readonly || saving">
                <template v-slot:activator="{ on, attrs }"><v-text-field :value="dateLabel(form.start_date)" label="วันเริ่ม · 08:30 น." prepend-inner-icon="mdi-calendar" readonly outlined hide-details v-bind="attrs" v-on="on" :disabled="readonly || saving" /></template>
                <v-date-picker v-model="form.start_date" locale="th-TH" @input="startMenu = false" />
              </v-menu>
            </v-col>
            <v-col cols="12" sm="6">
              <v-menu v-model="endMenu" :close-on-content-click="false" offset-y min-width="290px" :disabled="readonly || saving">
                <template v-slot:activator="{ on, attrs }"><v-text-field :value="dateLabel(form.end_date)" label="วันสิ้นสุด · 16:30 น." prepend-inner-icon="mdi-calendar" readonly outlined hide-details v-bind="attrs" v-on="on" :disabled="readonly || saving" /></template>
                <v-date-picker v-model="form.end_date" :min="form.start_date || undefined" locale="th-TH" @input="endMenu = false" />
              </v-menu>
            </v-col>
          </v-row>
          <v-textarea v-model="form.reason" label="เหตุผล" outlined auto-grow rows="3" maxlength="2000" hide-details class="mt-4" :disabled="readonly || saving" />
        </v-card-text>
        <v-card-actions class="flex-wrap">
          <v-btn v-if="form.id && !readonly" color="error" text :disabled="saving || usersLoading" @click="confirmCancel = true">ยกเลิกช่วงเวลานี้</v-btn>
          <v-spacer /><v-btn text :disabled="saving" @click="closeDialog">ปิด</v-btn>
          <v-btn v-if="!readonly" color="primary" :loading="saving" :disabled="usersLoading" @click="save">บันทึก</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="confirmCancel" max-width="480" persistent>
      <v-card><v-card-title>ยกเลิกช่วงเวลาพิเศษนี้?</v-card-title>
        <v-card-text>ผู้ใช้จะใช้ช่วงเวลานี้ไม่ได้อีก โดยยังเก็บรายการและรายชื่อไว้ดูย้อนหลัง</v-card-text>
        <v-card-actions><v-spacer /><v-btn text :disabled="saving" @click="confirmCancel = false">กลับ</v-btn><v-btn color="error" :loading="saving" @click="cancel">ยืนยันยกเลิก</v-btn></v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import ScheduleSpecialPeriodProvider from '@/services/schedule_special_period.provider';
const service = new ScheduleSpecialPeriodProvider();
const emptyForm = () => ({ id: null, revision: null, target_type: 'FACULTY', start_date: '', end_date: '', reason: '', users: [] });
export default {
  name: 'ConfigScheduleSpecialPeriod',
  props: { facultyRows: { type: Array, default: () => [] } },
  data: () => ({
    level: '1', faculty: null, year: String(new Date().getFullYear() + 543), semester: '1',
    rows: [], total: 0, page: 1, loading: false, saving: false, ready: false, version: 0,
    dialog: false, readonly: false, confirmCancel: false, form: emptyForm(), startMenu: false, endMenu: false,
    users: [], historicalUsers: [], userSearch: '', usersLoading: false, usersReady: false, userVersion: 0,
    levels: [{ text: 'ปริญญาตรี', value: '1' }, { text: 'บัณฑิตศึกษา', value: '2' }],
    headers: [{ text: 'ผู้ได้รับช่วงเวลาพิเศษ', value: 'TARGET_TYPE' }, { text: 'ช่วงเวลา', value: 'period' },
      { text: 'เหตุผล', value: 'REASON' }, { text: 'สถานะ', value: 'STATUS' }, { text: 'จัดการ', value: 'actions' }],
  }),
  computed: {
    busy() { return this.loading || this.saving || this.usersLoading; },
    scope() { return { level_id: this.level, faculty_id: this.faculty, academic_year: this.year, semester: this.semester }; },
    scopeReady() { return !!this.faculty && /^[1-9][0-9]{3}$/.test(this.year) && this.semesters.includes(this.semester); },
    semesters() { return this.level === '2' ? ['1', '2'] : ['1', '2', '3']; },
    faculties() { return this.facultyRows.filter(x => x.faculty_id !== 'AL').map(x => ({ value: String(x.faculty_id), text: x.faculty_name_th || x.faculty_name || String(x.faculty_id) })); },
    scopeLabel() { return `${this.levels.find(x => x.value === this.level).text} · ${(this.faculties.find(x => x.value === this.faculty) || {}).text || ''} · ภาคเรียน ${this.semester}/${this.year}`; },
    userOptions() {
      const current = new Set(this.users.map(x => x.USER_ID));
      return [...this.users, ...this.historicalUsers.filter(x => !current.has(x.USER_ID))].map(x => ({ ...x,
        label: `${[x.PREFIX_TH || x.PREFIX_EN, x.FULLNAME_TH || x.FULLNAME_EN].filter(Boolean).join(' ') || x.USER_ID} (${x.USER_ID})`,
        disabled: !this.readonly && !current.has(x.USER_ID),
      }));
    },
  },
  watch: {
    level() { if (!this.semesters.includes(this.semester)) this.semester = '1'; this.scopeChanged(); },
    faculty() { this.scopeChanged(); },
    semester() { this.scopeChanged(); },
    busy(value) { this.$emit('busy', value || this.dialog); },
    dialog(value) { this.$emit('busy', value || this.busy); },
  },
  beforeDestroy() { this.version++; this.userVersion++; this.$emit('busy', false); },
  methods: {
    message(error) { const text = error.response && error.response.data && error.response.data.message; return typeof text === 'string' ? text : 'ไม่สามารถดำเนินการได้ กรุณาลองใหม่'; },
    dateLabel(date) { if (!date) return ''; const [y, m, d] = date.split('-'); return `${d}/${m}/${Number(y) + 543}`; },
    formatDate(value) { return value ? `${this.dateLabel(value.slice(0, 10))} ${value.slice(11, 16)} น.` : ''; },
    statusInfo(status) { return ({ pending: { text: 'รอเปิด', color: 'warning' }, active: { text: 'กำลังเปิด', color: 'success' }, expired: { text: 'หมดเวลา', color: 'grey' }, cancelled: { text: 'ยกเลิกแล้ว', color: 'error' } })[status] || { text: '', color: 'grey' }; },
    scopeChanged() { this.page = 1; this.usersReady = false; return this.reload(); },
    async reload() {
      const version = ++this.version;
      this.rows = []; this.total = 0; this.ready = false; this.usersReady = false;
      if (!this.scopeReady) { this.loading = false; return; }
      this.loading = true;
      try {
        const result = await service.list(this.scope, this.page);
        if (version !== this.version) return;
        this.rows = result.items; this.total = result.total; this.ready = true;
      } catch (error) { if (version === this.version) this.$toasted.error(this.message(error)); }
      finally { if (version === this.version) this.loading = false; }
    },
    openCreate() { this.form = emptyForm(); this.historicalUsers = []; this.usersReady = false; this.users = []; this.readonly = false; this.dialog = true; },
    closeDialog() { this.userVersion++; this.usersLoading = false; this.dialog = false; this.confirmCancel = false; this.startMenu = false; this.endMenu = false; },
    async openDetail(item) {
      if (this.busy) return;
      this.loading = true;
      try {
        const detail = await service.detail(item.ID);
        const row = detail.item;
        this.form = { id: row.ID, revision: detail.revision, target_type: row.TARGET_TYPE, reason: row.REASON,
          start_date: row.START_DATETIME.slice(0, 10), end_date: row.END_DATETIME.slice(0, 10), users: detail.users.map(x => x.USER_ID) };
        this.historicalUsers = detail.users; this.users = []; this.usersReady = false;
        this.readonly = !!row.deleted_at; this.dialog = true;
        if (row.TARGET_TYPE === 'USERS' && !this.readonly) await this.loadUsers();
      } catch (error) { this.$toasted.error(this.message(error)); }
      finally { this.loading = false; }
    },
    async loadUsers() {
      if (this.usersReady) return;
      const version = ++this.userVersion;
      this.usersLoading = true;
      try {
        const users = await service.users(this.scope);
        if (version !== this.userVersion) return;
        this.users = users; this.usersReady = true;
      } catch (error) { if (version === this.userVersion) this.$toasted.error(this.message(error)); }
      finally { if (version === this.userVersion) this.usersLoading = false; }
    },
    targetChanged() { if (this.form.target_type === 'USERS') return this.loadUsers(); },
    selectAll() { if (this.users.length > 500) { this.$toasted.error('เลือกได้ไม่เกิน 500 คนต่อรายการ'); return; } this.form.users = this.users.map(x => x.USER_ID); },
    async save() {
      if (this.saving) return;
      if (!this.form.start_date || !this.form.end_date || this.form.start_date > this.form.end_date) { this.$toasted.error('กรุณาเลือกวันเริ่มและวันสิ้นสุดให้ถูกต้อง'); return; }
      if (!this.form.reason.trim()) { this.$toasted.error('กรุณาระบุเหตุผล'); return; }
      if (this.form.target_type === 'USERS' && (!this.usersReady || !this.form.users.length || this.form.users.length > 500)) { this.$toasted.error('กรุณาโหลดรายชื่อและเลือก 1–500 คน'); return; }
      this.saving = true;
      try {
        await service.save({ ...this.scope, ...this.form, users: this.form.target_type === 'USERS' ? this.form.users : [] });
        this.closeDialog(); this.$toasted.success('บันทึกช่วงเวลาพิเศษเรียบร้อยแล้ว'); await this.reload();
      } catch (error) { this.$toasted.error(this.message(error)); }
      finally { this.saving = false; }
    },
    async cancel() {
      if (this.saving) return;
      this.saving = true;
      try {
        await service.cancel(this.form.id, this.form.revision);
        this.closeDialog(); this.$toasted.success('ยกเลิกช่วงเวลาพิเศษเรียบร้อยแล้ว'); await this.reload();
      } catch (error) { this.$toasted.error(this.message(error)); }
      finally { this.saving = false; }
    },
  },
};
</script>
